import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const getTimestampNow = (): string => {
  const now = new Date();
  const timeString = now.toLocaleTimeString();
  return timeString;
};

export const getTodayDateString = (): string => dayjs().format('YYYY-MM-DD');

/**
 * Display formatters, for putting a date in front of a user.
 *
 * These parse, so a value carrying a zone (`...Z`) is converted to local time.
 * The `extract*` family below instead reads the literal text of a timestamp,
 * which is what you want when the backend's wall-clock time is the point. Pick
 * deliberately: for a `...Z` value the two disagree by the offset.
 *
 * There is a third name, `formatInstant`, at the bottom of this file. It is
 * `formatDateTime` plus a guard: an absent or unparseable value yields a
 * sentinel instead of the literal string "Invalid Date", which `formatDateTime`
 * happily returns and which the `extract*` family never produces. Reach for it
 * when the result goes straight into a table cell.
 */
export const formatDate = (date?: string | Date | null): string =>
  date ? dayjs(date).format('DD.MM.YYYY') : '';

export const formatDateTime = (date?: string | Date | null): string =>
  date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '';

export const formatTime = (time?: string | Date | null): string =>
  time ? dayjs(time).format('HH:mm') : '';

export const isToday = (value: string | null | undefined): boolean =>
  Boolean(value) && extractDate(value) === getTodayDateString();

/** True when the instant is still ahead of now; false for a missing or invalid value. */
export const isAhead = (value: string | null | undefined): value is string => {
  if (!value) {
    return false;
  }
  const instant = dayjs(value);
  return instant.isValid() && instant.diff(dayjs()) > 0;
};

export const minutesToSeconds = (
  minutes: number | null | undefined
): number => {
  if (minutes === null || minutes === undefined) {
    return 0;
  }
  if (!Number.isFinite(minutes)) {
    return 0;
  }
  return minutes * 60;
};

export const secondsToMinutes = (
  seconds: number | string | null | undefined
): number => {
  if (seconds === null || seconds === undefined || seconds === '') {
    return 0;
  }
  const numericSeconds =
    typeof seconds === 'number' ? seconds : Number(seconds);
  if (!Number.isFinite(numericSeconds)) {
    return 0;
  }
  return numericSeconds / 60;
};
/**
 * Extracts the date part from a datetime string
 * @param dateTimeString - DateTime string in format "YYYY-MM-DDTHH:mm:ss" or similar
 * @returns Date string "YYYY-MM-DD" or empty string if invalid
 */
export const extractDate = (
  dateTimeString: string | null | undefined
): string => {
  if (!dateTimeString) {
    return '';
  }

  const datePart = dateTimeString.split('T')[0];
  if (!datePart) {
    return '';
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(datePart) ? datePart : '';
};

/**
 * Extracts date and time (HH:mm) from a datetime string
 * @param dateTimeString - DateTime string in format "YYYY-MM-DDTHH:mm:ss" or similar
 * @returns Formatted string "YYYY-MM-DD HH:mm" or empty string if invalid
 */
export const extractDateTime = (
  dateTimeString: string | null | undefined
): string => {
  const date = extractDate(dateTimeString);
  const time = extractHoursMinutes(dateTimeString);
  if (!date && !time) {
    return '';
  }
  return [date, time].filter(Boolean).join(' ');
};

/**
 * Extracts hours and minutes from a datetime string in format "HH:mm"
 * @param dateTimeString - DateTime string in format "YYYY-MM-DDTHH:mm:ss" or similar
 * @returns Formatted time string "HH:mm" or empty string if invalid
 */
export const extractHoursMinutes = (
  dateTimeString: string | null | undefined
): string => {
  if (!dateTimeString) {
    return '';
  }

  try {
    const timePart = dateTimeString.split('T')[1];
    if (!timePart) {
      return '';
    }

    const hoursMinutes = timePart.substring(0, 5);

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (timeRegex.test(hoursMinutes)) {
      return hoursMinutes;
    }

    return '';
  } catch {
    return '';
  }
};

export const isValidDateTime = (value: string) => {
  // Must start with ISO-like date pattern
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return false;
  }

  const parsed = dayjs.utc(value); // Changed from dayjs(value)

  // Verify it parsed correctly and isn't just fallback to "now"
  return (
    parsed.isValid() && parsed.format('YYYY-MM-DD') === value.substring(0, 10)
  );
};

export const formatSecondsToTime = (input?: number | string): string => {
  if (input === undefined || input === null || input === '') {
    return '';
  }

  const formatTime = (totalSeconds: number): string => {
    const isNegative = totalSeconds < 0;
    const absSeconds = Math.abs(totalSeconds);
    const minutes = Math.floor(absSeconds / 60);
    const seconds = absSeconds % 60;
    return `${isNegative ? '-' : ''}${minutes}m ${seconds}s`;
  };

  // Handle number input
  if (typeof input === 'number') {
    if (isNaN(input) || !isFinite(input) || !Number.isInteger(input)) {
      return '';
    }
    return formatTime(input);
  }

  // Handle string input
  const trimmedInput = input.trim();

  // Check for plain number string (including negative)
  const plainNumberMatch = trimmedInput.match(/^(-?\d+)$/);
  if (plainNumberMatch && plainNumberMatch[1]) {
    const num = parseInt(plainNumberMatch[1], 10);
    return formatTime(num);
  }

  // Check for minutes only format like "5m" or "-5m"
  const minutesOnlyMatch = trimmedInput.match(/^(-?\d+)m$/i);
  if (minutesOnlyMatch && minutesOnlyMatch[1]) {
    const minutes = parseInt(minutesOnlyMatch[1], 10);
    return `${minutes}m 0s`;
  }

  // Check for seconds format like "300s" or "-300s"
  const secondsMatch = trimmedInput.match(/^(-?\d+)s$/i);
  if (secondsMatch && secondsMatch[1]) {
    const num = parseInt(secondsMatch[1], 10);
    return formatTime(num);
  }

  const timeMatch = trimmedInput.match(/^(-?\d+)m\s*(\d+)s$/i);
  if (timeMatch && timeMatch[1] && timeMatch[2]) {
    const minutes = parseInt(timeMatch[1], 10);
    const seconds = parseInt(timeMatch[2], 10);

    const isInputNegative = trimmedInput.trim().startsWith('-');

    let totalSeconds;
    if (isInputNegative && minutes === 0) {
      totalSeconds = -seconds;
    } else {
      totalSeconds = minutes * 60 + (minutes < 0 ? -seconds : seconds);
    }

    if (seconds > 59) {
      return formatTime(totalSeconds);
    }

    return formatTime(totalSeconds);
  }

  return '';
};

// Backend emits compact UTC timestamps as "yyyyMMdd-HH:mm" (invariant culture),
// both as standalone parameter values and embedded in free-text messages.
const compactUtcTimestamp = /(\d{4})(\d{2})(\d{2})-(\d{2}):(\d{2})/g;

/**
 * Reformats every compact UTC timestamp ("yyyyMMdd-HH:mm") found in the input
 * to display form ("dd.MM.yyyy HH:mm"), leaving the rest of the string
 * untouched. A trailing " UTC" literal is preserved as surrounding text.
 */
export const formatCompactUtcTimestamps = (value: string): string =>
  value.replace(
    compactUtcTimestamp,
    (_match, year, month, day, hour, minute) =>
      `${day}.${month}.${year} ${hour}:${minute}`
  );

export const parseTimeToSeconds = (timeString?: string): number | undefined => {
  if (!timeString) {
    return undefined;
  }

  // Use ^ and $ to ensure exact match
  const match = timeString.match(/^(-?\d+)m\s*(\d+)s$/);

  if (match && match[1] && match[2]) {
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    const totalSeconds = Math.abs(minutes) * 60 + seconds;

    // Handle -0m 0s special case - should return 0, not -0
    if (totalSeconds === 0) {
      return 0;
    }

    return timeString.trim().startsWith('-') ? -totalSeconds : totalSeconds;
  }

  return undefined;
};

/** Rendered in place of a timestamp that is absent or cannot be parsed. */
export const MISSING_VALUE = '\u2014';

/**
 * Formats a backend instant for display, converted to the viewer's local zone.
 *
 * Use this for values that carry their own zone — either a `Z` suffix or an
 * explicit offset such as `+02:00`. Ruter is inconsistent about which it sends
 * (deviation durations carry an offset, lifecycle timestamps are UTC), and
 * parsing normalises both to one zone so they can be read side by side.
 *
 * A value with no zone at all is interpreted as already being local, which is
 * dayjs's own behaviour and a round trip for display purposes.
 *
 * Unlike `formatDateTime`, an absent or unparseable value yields `fallback`
 * rather than the literal string "Invalid Date".
 */
export const formatInstant = (
  value?: string | Date | null,
  fallback: string = MISSING_VALUE
): string => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : fallback;
};
